import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import api from '../../config/api';
import { useAuth } from '../../context/AuthContext';

// 2-step verification (authenticator app) on the Profile page. Backend: user-management/mfa.js.
const TwoStepVerificationCard = () => {
  const { adoptSessionToken } = useAuth();
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);           // { qr_data_url, secret } while enrolling
  const [recoveryCodes, setRecoveryCodes] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [disabling, setDisabling] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get('/api/users/mfa/status');
      setStatus(data);
    } catch (e) {
      setError(e.response?.data?.message || 'Could not load 2-step verification status');
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/api/users/mfa/setup');
      setSetup(data);
      setCode('');
    } catch (e) {
      setError(e.response?.data?.message || 'Could not start setup');
    } finally { setBusy(false); }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      const { data } = await api.post('/api/users/mfa/enable', { code: code.trim() });
      adoptSessionToken(data.token);
      setRecoveryCodes(data.recovery_codes);
      setSetup(null);
      setCode('');
      await loadStatus();
    } catch (e) {
      setError(e.response?.data?.message || 'That code is not valid');
    } finally { setBusy(false); }
  };

  const confirmDisable = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api.post('/api/users/mfa/disable', { code: code.trim() });
      setDisabling(false);
      setCode('');
      setRecoveryCodes(null);
      await loadStatus();
    } catch (e) {
      setError(e.response?.data?.message || 'That code is not valid');
    } finally { setBusy(false); }
  };

  const codeField = (
    <TextField
      fullWidth autoFocus
      label="6-digit code"
      autoComplete="one-time-code"
      inputProps={{ inputMode: 'numeric', maxLength: 6 }}
      value={code}
      onChange={(e) => { setCode(e.target.value); setError(''); }}
      sx={{ mb: 2, maxWidth: 240 }}
    />
  );

  return (
    <Paper sx={{ borderRadius: 3, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)', p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
        2-step verification
      </Typography>
      <Typography sx={{ color: 'text.secondary', mb: 3 }}>
        After your password (or Google / Microsoft), sign-in also asks for a code from an authenticator app
        such as Google Authenticator, Microsoft Authenticator or 1Password.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {!status && !error && <CircularProgress size={24} />}

      {recoveryCodes && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600, mb: 1 }}>Save these recovery codes now. They are shown only once.</Typography>
          <Typography sx={{ mb: 1 }}>Each one signs you in once if you lose your phone.</Typography>
          <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: '0.95rem' }}>
            {recoveryCodes.join('\n')}
          </Box>
        </Alert>
      )}

      {status && !status.enabled && !setup && (
        <>
          {status.required && (
            <Alert severity="info" sx={{ mb: 3 }}>2-step verification is required for your account. Please turn it on.</Alert>
          )}
          <Button variant="contained" onClick={startSetup} disabled={busy} sx={{ borderRadius: 2 }}>
            Turn on 2-step verification
          </Button>
        </>
      )}

      {setup && (
        <Box component="form" onSubmit={confirmSetup}>
          <Typography sx={{ mb: 2 }}>1. Scan this QR code with your authenticator app.</Typography>
          <Box component="img" src={setup.qr_data_url} alt="Authenticator QR code" sx={{ width: 200, height: 200, mb: 1 }} />
          <Typography sx={{ color: 'text.secondary', fontSize: '0.85rem', mb: 3 }}>
            Can't scan? Enter this key instead: <Box component="span" sx={{ fontFamily: 'monospace' }}>{setup.secret}</Box>
          </Typography>
          <Typography sx={{ mb: 2 }}>2. Enter the 6-digit code the app shows.</Typography>
          {codeField}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button type="submit" variant="contained" disabled={busy || !code.trim()} sx={{ borderRadius: 2 }}>Confirm</Button>
            <Button variant="outlined" onClick={() => { setSetup(null); setCode(''); setError(''); }} sx={{ borderRadius: 2 }}>Cancel</Button>
          </Box>
        </Box>
      )}

      {status && status.enabled && (
        <>
          <Alert severity="success" sx={{ mb: 3 }}>
            2-step verification is on{status.enrolled_at ? ` since ${String(status.enrolled_at).slice(0, 10)}` : ''}.
            {' '}{status.recovery_codes_left} recovery code{status.recovery_codes_left === 1 ? '' : 's'} left.
          </Alert>
          {!status.required && !disabling && (
            <Button variant="outlined" color="secondary" onClick={() => { setDisabling(true); setCode(''); }} sx={{ borderRadius: 2 }}>
              Turn off
            </Button>
          )}
          {disabling && (
            <Box component="form" onSubmit={confirmDisable}>
              <Typography sx={{ mb: 2 }}>Enter a current code to turn 2-step verification off.</Typography>
              {codeField}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button type="submit" variant="contained" color="secondary" disabled={busy || !code.trim()} sx={{ borderRadius: 2 }}>Turn off</Button>
                <Button variant="outlined" onClick={() => { setDisabling(false); setCode(''); setError(''); }} sx={{ borderRadius: 2 }}>Cancel</Button>
              </Box>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default TwoStepVerificationCard;
